import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowRight, Calendar, MapPin } from "lucide-react";
import { format } from "date-fns";

const STATUS_COLOR = {
  confirmed: "text-[#34D399] border-[#34D399]/40",
  pending: "text-[#FBBF24] border-[#FBBF24]/40",
  cancelled: "text-[#F87171] border-[#F87171]/40",
  completed: "text-white/60 border-white/20",
};

export default function Trips() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/bookings")
      .then((r) => setBookings(r.data))
      .catch(() => setBookings([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = bookings.filter((b) => {
    if (filter === "all") return true;
    if (filter === "upcoming") return b.status === "confirmed" || b.status === "pending";
    return b.status === filter;
  });

  return (
    <div data-testid="trips-page" className="min-h-screen bg-[#0A0A0A] pt-28 pb-20">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="text-[11px] tracking-[0.4em] uppercase text-[#C5A059]">My Reservations</div>
            <h1 className="font-heading text-5xl sm:text-6xl text-white mt-3 leading-[1.05]">Your trips.</h1>
          </div>
          <Link
            to="/book"
            data-testid="trips-new-booking-btn"
            className="inline-flex items-center gap-2 bg-white text-[#0A0A0A] hover:bg-[#C5A059] hover:text-white transition-colors duration-300 px-8 py-4 text-xs uppercase tracking-[0.3em] self-start"
          >
            Book a New Ride <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="flex flex-wrap gap-2 mb-8 border-b border-white/10 pb-4">
          {["all", "upcoming", "cancelled", "completed"].map((f) => (
            <button
              key={f}
              data-testid={`trips-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`text-[10px] uppercase tracking-[0.25em] px-4 py-2 border transition-colors ${
                filter === f ? "border-[#C5A059] text-[#C5A059]" : "border-white/10 text-white/60 hover:border-white/30"
              }`}
            >{f}</button>
          ))}
        </div>

        {loading ? (
          <div className="text-white/60">Loading…</div>
        ) : filtered.length === 0 ? (
          <div data-testid="trips-empty-state" className="border border-white/10 p-16 text-center">
            <div className="font-heading text-2xl text-white">No reservations yet.</div>
            <p className="text-white/60 mt-2 text-sm">Book your first ride and it will appear here.</p>
            <Link
              to="/book"
              className="inline-block mt-6 border border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-white px-6 py-3 text-xs uppercase tracking-[0.3em] transition-colors"
            >
              Reserve a Car
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((b) => (
              <Link
                key={b.id}
                to={`/trips/${b.id}`}
                data-testid={`trip-row-${b.id}`}
                className="block bg-[#141414] border border-white/10 hover:border-[#C5A059]/40 transition-colors p-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-center"
              >
                <div className="md:col-span-2">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Code</div>
                  <div className="font-mono-display text-[#C5A059] mt-1">{b.confirmation_code}</div>
                </div>
                <div className="md:col-span-3">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">When</div>
                  <div className="text-white text-sm mt-1 flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-[#C5A059]" /> {format(new Date(b.pickup_datetime), "MMM d, p")}</div>
                </div>
                <div className="md:col-span-4">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Route</div>
                  <div className="text-white text-sm mt-1 flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-[#C5A059] shrink-0" /> <span className="truncate">{b.pickup_address}{b.dropoff_address ? ` → ${b.dropoff_address}` : ""}</span></div>
                </div>
                <div className="md:col-span-2">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Vehicle</div>
                  <div className="text-white text-sm mt-1 truncate">{b.vehicle_class}</div>
                </div>
                <div className="md:col-span-1 md:text-right">
                  <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-1 border ${STATUS_COLOR[b.status] || ""}`}>
                    {b.status}
                  </span>
                  <div className="font-heading text-lg text-white mt-2">${b.estimated_fare.toFixed(0)}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
